import React from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Edit } from 'lucide-react'
import SofaBlueprint from '@/configurator/SofaBlueprint'
import SofaFormEditForm from './SofaFormEditForm'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { DataTable } from '@/components/shared/data-table/data-table'
import { DataTableToolbar } from '@/components/shared/data-table/data-table-toolbar'
import { ColumnDef } from '@tanstack/react-table'
import { gql, useQuery } from '@apollo/client'

const GET_SOFA_FORMS = gql`
  query GetSofaForms($productId: Int!) {
    findUniqueProductContainer(where: { id: $productId }) {
      advanced_product {
        sofa_forms {
          id
          code
          enabled
          left_and_right
          name
          type
          dimensions {
            height
            width
            length
            armrest_width
            backrest_width
            mattress_width
            mattress_length
            corner_part_length
            angle
            seat_height
            seat_depth
            seat_width
            extendable_part_length
            number_of_big_pillows
            number_of_small_pillows
            spread_of_big_pillows
            spread_of_small_pillows
            size_of_big_pillow
            size_of_pillow
            composition
            corner_radius
            seat_sections
            backrest_sections
            extension_type
            backrest_type
            covered_side
          }
          package_dimensions {
            id
            volume
            name
          }
        }
        _count {
          sofa_forms
        }
      }
    }
  }
`

interface PackageDimension {
  id: string
  volume: number
  name: string
}

interface SofaForm {
  id: string
  code: string
  enabled: boolean
  left_and_right: boolean
  name: string
  type: string
  dimensions: {
    width?: number | null
    length?: number | null
    armrest_width?: number | null
    backrest_width?: number | null
    mattress_width?: number | null
    mattress_length?: number | null
    corner_part_length?: number | null
    angle?: number | null
    seat_height?: number | null
    extendable_part_length?: number | null
    number_of_big_pillows?: number | null
    number_of_small_pillows?: number | null
    spread_of_big_pillows?: number | null
    spread_of_small_pillows?: number | null
    size_of_big_pillow?: number | null
    size_of_pillow?: number | null
  }
  package_dimensions: PackageDimension[]
}

interface SofaFormsTableProps {
  productId: number
  initialCount: number
}

export function SofaFormsTable({
  productId,
  initialCount,
}: SofaFormsTableProps) {
  const openSofaFormEdit = useOpenDrawer(SofaFormEditForm)

  const { loading, error, data } = useQuery(GET_SOFA_FORMS, {
    variables: { productId },
    skip: initialCount === 0,
  })

  const sofaForms =
    data?.findUniqueProductContainer?.advanced_product?.sofa_forms || []

  const columns: ColumnDef<SofaForm>[] = [
    {
      accessorKey: 'blueprint',
      header: 'Blueprint',
      cell: ({ row }) => (
        <SofaBlueprint
          id={parseInt(row.original.id)}
          dimensions={row.original.dimensions}
          type={row.original.type}
          containerHeight="60px"
          containerWidth="60px"
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => row.original.name,
      filterFn: (row, id, value) => {
        const searchValue = value.toLowerCase()
        const name = row.getValue(id) as string
        const code = row.original.code
        return (
          name.toLowerCase().includes(searchValue) ||
          code.toLowerCase().includes(searchValue)
        )
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <div
          className={`space-x-2 ${!row.original.enabled ? 'opacity-30' : ''}`}
        >
          <Badge variant="outline">{row.original.type}</Badge>
          {row.original.left_and_right && (
            <Badge variant="outline">Left & Right</Badge>
          )}
          <span className="text-muted-foreground">{row.original.code}</span>
        </div>
      ),
    },
    {
      accessorKey: 'package_dimensions',
      header: 'Volume',
      cell: ({ row }) => {
        const totalVolume = row.original.package_dimensions.reduce(
          (sum, dim) => sum + (dim.volume || 0),
          0
        )
        return <div>{totalVolume.toFixed(2)} m³</div>
      },
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              console.log('Edit Sofa Form', row.original)
              openSofaFormEdit(row.original)
            }}
          >
            <Edit />
          </Button>
        </div>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ]

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Modules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-24 flex items-center justify-center">
            Loading...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Modules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">Error loading sofa forms</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Modules</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={sofaForms}
          defaultPageSize={10}
          toolbar={table => (
            <DataTableToolbar
              table={table}
              config={{
                search: {
                  placeholder: 'Search modules by name or code...',
                  accessor: 'name',
                },
                columns: [],
              }}
            />
          )}
        />
      </CardContent>
    </Card>
  )
}
